import { TriggerCreate } from "@dots-projects/types";
import { v4 as uuidv4 } from "uuid";
import gif from "./public-onboarding-gif.gif";

export const publicOnboardingTemplate = (
  workspace: string
): {
  trigger: TriggerCreate;
  gif: any;
  title: string;
  description: string;
} => {
  return {
    title: "Public channel onboarding",
    description:
      '<p>Allow new members to onboard themselves by clicking a button in a public channel! With this template, you can introduce members to your server through interactive questions - allowing you to learn more about them, and point to the right content. These questions will take place in a public channel and will only be visible to the user. We recommend turning off send message functionality for all members for this channel.</p><p className="mt-10">Recommended for large communities. Explore the logic of this template in the editor view on the left.</p>',
    gif,
    trigger: {
      type: "button_click",
      platform: "discord",
      isDraft: true,
      communityId: localStorage.getItem("guild-id") || "",
      authorUserId: localStorage.getItem("discord-id") || "",
      workspace,
      name: "Public Channel Onboarding",
      configs: {
        messages: [
          {
            id: uuidv4(),
            buttonIds: [uuidv4()],
          },
        ],
        responseOnError: "Something went wrong, please try again!",
        $$editorButtonClickContent: {
          message:
            "Welcome to the community! Click here to get access to the rest of the server.",
          channelId: "$channelId",
          buttonText: "Button text",
          buttonEmoji: "👋",
          htmlMessage:
            "<p>Welcome to the community! Click here to get access to the rest of the server.</p>",
        },
      },
      steps: [
        {
          id: "sanket-test-server-ephemeral-demo-step3-troubleshoot",
          type: "send_message",
          configs: {
            message: "Thanks for being an early supporter! ",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "sanket-test-server-ephemeral-demo-final-message",
            },
          ],
        },
        {
          id: "sanket-test-server-ephemeral-demo-continent-question-step",
          name: "",
          type: "send_survey",
          configs: {
            channelId: `{{interaction.channelId}}`,
            type: "buttons",
            message:
              "Glad to have you here. Where in the world are you located?",
            options: [
              { id: "na", emoji: "🟢", label: "North America" },
              { id: "sa", emoji: "⚪", label: "South America" },
              { id: "asia", emoji: "🟣", label: "Asia" },
              { id: "africa", emoji: "🟡", label: "Africa" },
              { id: "europe", emoji: "🟠", label: "Europe" },
              { id: "oceania", emoji: "🟤", label: "Oceania" },
              { id: "skip", label: "Prefer not to say" },
            ],
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>Glad to have you here. Where in the world are you located?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "sa",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "asia",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "africa",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "europe",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "oceania",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
            {
              targetStepId: "sanket-test-server-ephemeral-demo-brings-you-here",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "skip",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-continent-question-step.messageId}}",
                },
              },
            },
          ],
        },
        {
          id: "sanket-test-server-ephemeral-demo-brings-you-here",
          name: "",
          type: "send_survey",
          configs: {
            type: "buttons",
            message: "Awesome! And, what brings you here?",
            options: [
              {
                id: "learn",
                emoji: "🎓",
                label: "Learn about your product",
              },
              {
                id: "others",
                emoji: "👥",
                label: "See what others are building",
              },
              {
                id: "trouble",
                emoji: "💻",
                label: "Troubleshoot",
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage: "<p>Awesome! And, what brings you here?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "sanket-test-server-ephemeral-demo-step3-student",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "learn",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-brings-you-here.messageId}}",
                },
              },
            },
            {
              targetStepId:
                "sanket-test-server-ephemeral-demo-step3-see-others",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "others",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-brings-you-here.messageId}}",
                },
              },
            },
            {
              targetStepId:
                "sanket-test-server-ephemeral-demo-step3-troubleshoot",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "trouble",
                  messageId:
                    "{{sanket-test-server-ephemeral-demo-brings-you-here.messageId}}",
                },
              },
            },
          ],
        },
        {
          id: "sanket-test-server-ephemeral-demo-step3-student",
          name: "",
          type: "send_message",
          configs: {
            message: "Amazing! Here's info on our product ->",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>Amazing! Here's info on our product -&gt;</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "sanket-test-server-ephemeral-demo-final-message",
            },
          ],
        },
        {
          id: "sanket-test-server-ephemeral-demo-step3-see-others",
          name: "",
          type: "send_message",
          configs: {
            message:
              "Amazing! Please join the discussion in our general channel.",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>Amazing! Please join the discussion in our general channel.</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "sanket-test-server-ephemeral-demo-final-message",
            },
          ],
        },
        {
          id: "sanket-test-server-ephemeral-demo-final-message",
          type: "send_message",
          configs: {
            message:
              "\n\n Great! Thanks for being part of our community! Let us know if you have questions or feedback!",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
          },
          isEntry: false,
          isGoal: true,
          transitions: [
            {
              targetStepId: "send_survey-0-4R8VMy",
            },
          ],
        },
        {
          id: "send_message-0-71j4gj",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Hey {{user.username}} !",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              '<p>Hey <span class="mention" data-index="0" data-denotation-char="{{" data-id="user.username" data-value="username}}">﻿<span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>username}}</span>﻿</span> ! </p>',
          },
          isEntry: true,
          isGoal: false,
          transitions: [
            {
              targetStepId:
                "sanket-test-server-ephemeral-demo-continent-question-step",
            },
          ],
        },
        {
          id: "send_survey-0-4R8VMy",
          name: "Send a survey (single selection)",
          type: "send_survey",
          configs: {
            type: "buttons",
            message:
              "Would you be open to sharing your email address so we can keep you updated? We won't spam you :)",
            options: [
              {
                id: "no",
                emoji: "🟡",
                label: "No",
                skipDefer: true,
              },
              {
                id: "yes",
                emoji: "🟢",
                label: "Yes",
                skipDefer: true,
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>Would you be open to sharing your email address so we can keep you updated? We won't spam you :) </p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "usi34id2-email-modal",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "yes",
                },
              },
            },
            {
              targetStepId: "send_message-0-P4GA0F",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "no",
                },
              },
            },
          ],
        },
        {
          id: "usi34id2-email-modal",
          name: "Show a modal",
          type: "open_modal",
          configs: {
            title: "Email address",
            fields: [
              {
                id: "emailAdress",
                type: "open_modal",
                label: "Please share your email!",
                style: "short",
              },
            ],
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-P4GA0F",
              when: {
                userResponse: {
                  type: "modal-submit",
                  modalId: "{{usi34id2-email-modal.modalId}}",
                  userId: "{{user.id}}",
                  messageId: "{{usi34id2-email-modal.messageId}}",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-P4GA0F",
          name: "Send a message",
          type: "send_message",
          configs: {
            message: "Thanks for joining us!",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage: "<p>Thanks for joining us! </p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [],
        },
      ],
    },
  };
};
